import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveProjectRecordId, resolveProjectRestoreDecision } from './project-selection.js';
import {
  clearWorkspaceLocation,
  readWorkspaceLocation,
  resolveWorkspaceLocationStorageKey,
  saveWorkspaceLocation,
} from './workspace-location-storage.js';

const EMPTY_LIST = Object.freeze([]);

function text(value) {
  return String(value || '').trim();
}

function projectIdentity(value) {
  return typeof value === 'string' ? text(value) : resolveProjectRecordId(value);
}

function sessionIdentity(record = {}) {
  return text(record?.sessionId || record?.session_id || record?.id);
}

function sessionProjectIdentity(record = {}) {
  return text(record?.projectId || record?.project_id);
}

function projectListState(sessionList, projectItems) {
  if (sessionList?.bootStatus === 'failed') {
    return 'failed';
  }
  if (sessionList?.projectLoading || (sessionList?.bootStatus === 'pending' && projectItems.length === 0)) {
    return 'pending';
  }
  return 'ready';
}

export function shouldClearRestoredSession(error) {
  return new Set([401, 403, 404]).has(Number(error?.status));
}

export function restoredSessionMatchesScope(session = {}, scope = {}) {
  return Boolean(
    sessionIdentity(session)
    && sessionIdentity(session) === text(scope.sessionId)
    && sessionProjectIdentity(session) === text(scope.projectId)
  );
}

export function resolveWorkspaceSessionDecision(input) {
  const source = Object(input);
  if (source.suppressSessionRestore) return { action: 'wait', reason: 'explicit_navigation', sessionId: '' };
  const activeSessionId = text(source.activeSessionId);
  if (activeSessionId) {
    return { action: 'keep', reason: 'active_session_present', sessionId: activeSessionId };
  }
  if (source.projectReady !== true) {
    return { action: 'wait', reason: 'project_not_ready', sessionId: '' };
  }
  const location = Object(source.location);
  const storedProjectId = text(location.projectId);
  const storedSessionId = text(location.sessionId);
  if (!storedSessionId) {
    return { action: 'blank', reason: 'stored_session_absent', sessionId: '' };
  }
  if (storedProjectId !== text(source.effectiveProjectId)) {
    return { action: 'blank', reason: 'stored_session_project_mismatch', sessionId: '' };
  }
  if (text(source.attemptedSessionId) === storedSessionId) {
    return { action: 'wait', reason: 'stored_session_attempted', sessionId: storedSessionId };
  }
  return { action: 'select', reason: 'stored_session_candidate', sessionId: storedSessionId };
}

export function useWorkspaceLocationSelection({ instanceId = '', projectId = '', userId = '' } = {}) {
  const identity = useMemo(() => ({ instanceId: text(instanceId), userId: text(userId) }), [instanceId, userId]);
  const scopeKey = resolveWorkspaceLocationStorageKey(identity);
  const explicitProjectId = text(projectId);
  const [locationState, setLocationState] = useState(() => ({
    scopeKey,
    value: readWorkspaceLocation(identity),
  }));
  const [selectionState, setSelectionState] = useState(() => ({ scopeKey, projectId: explicitProjectId }));
  const location = locationState.scopeKey === scopeKey
    ? locationState.value
    : readWorkspaceLocation(identity);
  const selectedProjectId = selectionState.scopeKey === scopeKey ? selectionState.projectId : '';
  const effectiveProjectId = text(selectedProjectId || explicitProjectId);

  useEffect(() => {
    if (locationState.scopeKey === scopeKey && selectionState.scopeKey === scopeKey) {
      return;
    }
    setLocationState({ scopeKey, value: readWorkspaceLocation(identity) });
    setSelectionState({ scopeKey, projectId: explicitProjectId });
  }, [explicitProjectId, identity, locationState.scopeKey, scopeKey, selectionState.scopeKey]);

  const persistLocation = useCallback((nextProjectId = '', nextSessionId = '') => {
    const value = { projectId: text(nextProjectId), sessionId: text(nextSessionId) };
    setLocationState((previous) => (
      previous.scopeKey === scopeKey
      && previous.value.projectId === value.projectId
      && previous.value.sessionId === value.sessionId
        ? previous
        : { scopeKey, value }
    ));
    if (value.projectId) {
      saveWorkspaceLocation(identity, value);
    } else {
      clearWorkspaceLocation(identity);
    }
  }, [identity, scopeKey]);

  const selectProject = useCallback((project) => {
    const nextProjectId = projectIdentity(project);
    setSelectionState((previous) => (
      previous.scopeKey === scopeKey && previous.projectId === nextProjectId
        ? previous
        : { scopeKey, projectId: nextProjectId }
    ));
    persistLocation(nextProjectId, '');
  }, [persistLocation, scopeKey]);

  const restoreProject = useCallback((nextProjectId, nextSessionId = '') => {
    const restoredProjectId = text(nextProjectId);
    setSelectionState((previous) => (
      previous.scopeKey === scopeKey && previous.projectId === restoredProjectId
        ? previous
        : { scopeKey, projectId: restoredProjectId }
    ));
    persistLocation(restoredProjectId, nextSessionId);
  }, [persistLocation, scopeKey]);

  return {
    effectiveProjectId,
    explicitProjectId,
    location,
    persistLocation,
    restoreProject,
    scopeKey,
    selectProject,
  };
}

function sessionCanPersist(sessionItems, location, projectId, sessionId) {
  if (text(location?.projectId) === projectId && text(location?.sessionId) === sessionId) {
    return true;
  }
  return sessionItems.some((item) => (
    sessionIdentity(item) === sessionId && sessionProjectIdentity(item) === projectId
  ));
}

function listItems(value) {
  return Array.isArray(value) ? value : EMPTY_LIST;
}

function requestNextProjectPage({
  loadMoreRequestRef,
  onLoadMoreProjects,
  projectItems,
  scopeKey,
  storedProjectId,
}) {
  if (typeof onLoadMoreProjects !== 'function') {
    return;
  }
  const requestKey = `${scopeKey}::${text(storedProjectId)}::${projectItems.length}`;
  if (loadMoreRequestRef.current === requestKey) {
    return;
  }
  loadMoreRequestRef.current = requestKey;
  void Promise.resolve(onLoadMoreProjects()).catch(() => {
    if (loadMoreRequestRef.current === requestKey) {
      loadMoreRequestRef.current = '';
    }
  });
}

function applyProjectRestoreDecision({
  decision,
  loadMoreRequestRef,
  location,
  onLoadMoreProjects,
  projectItems,
  restoreProject,
  scopeKey,
}) {
  const actions = {
    clear: () => restoreProject?.('', ''),
    load_more: () => requestNextProjectPage({
      loadMoreRequestRef,
      onLoadMoreProjects,
      projectItems,
      scopeKey,
      storedProjectId: location.projectId,
    }),
    select: () => restoreProject?.(
      decision.projectId,
      decision.reason === 'stored_project_visible' ? location.sessionId : '',
    ),
  };
  actions[decision.action]?.();
}

export function useWorkspaceLocationRestore({ sessionList = {}, workspaceLocation = {} } = {}) {
  const loadMoreRequestRef = useRef('');
  const attemptedSessionKeyRef = useRef('');
  const currentScopeRef = useRef('');
  const projectItems = useMemo(() => listItems(sessionList.projectItems), [sessionList.projectItems]);
  const sessionItems = useMemo(() => listItems(sessionList.sessions), [sessionList.sessions]);
  const location = useMemo(() => Object(workspaceLocation.location), [workspaceLocation.location]);
  const effectiveProjectId = text(workspaceLocation.effectiveProjectId);
  const explicitProjectId = text(workspaceLocation.explicitProjectId);
  const persistLocation = workspaceLocation.persistLocation;
  const restoreProject = workspaceLocation.restoreProject;
  const scopeKey = text(workspaceLocation.scopeKey);
  const activeSessionId = text(sessionList.activeSessionId);
  const bootStatus = text(sessionList.bootStatus);
  const suppressSessionRestore = sessionList.sidebarNavigation?.suppressSessionRestore === true;
  const onCreateSession = sessionList.onCreateSession;
  const onLoadMoreProjects = sessionList.onLoadMoreProjects;
  const onSelectSession = sessionList.onSelectSession;
  const projectHasMore = sessionList.projectHasMore === true;
  const projectLoading = sessionList.projectLoading === true;
  const currentScope = `${scopeKey}::${effectiveProjectId}`;
  currentScopeRef.current = currentScope;

  useEffect(() => {
    const decision = resolveProjectRestoreDecision({
      currentProjectId: effectiveProjectId,
      listState: projectListState({ bootStatus, projectLoading }, projectItems),
      projectHasMore,
      projectItems,
      storedProjectId: explicitProjectId ? '' : location.projectId,
    });
    applyProjectRestoreDecision({
      decision,
      loadMoreRequestRef,
      location,
      onLoadMoreProjects,
      projectItems,
      restoreProject,
      scopeKey,
    });
  }, [bootStatus, effectiveProjectId, explicitProjectId, location, onLoadMoreProjects, projectHasMore, projectItems, projectLoading, restoreProject, scopeKey]);

  useEffect(() => {
    if (explicitProjectId) {
      return;
    }
    const attemptedSessionId = attemptedSessionKeyRef.current.startsWith(`${currentScope}::`)
      ? attemptedSessionKeyRef.current.slice(currentScope.length + 2)
      : '';
    const decision = resolveWorkspaceSessionDecision({
      activeSessionId,
      suppressSessionRestore,
      attemptedSessionId,
      effectiveProjectId,
      location,
      projectReady: bootStatus === 'ready' && Boolean(effectiveProjectId),
    });
    if (decision.action !== 'select' || typeof onSelectSession !== 'function') {
      return;
    }
    const attemptScope = currentScope;
    attemptedSessionKeyRef.current = `${attemptScope}::${decision.sessionId}`;
    void Promise.resolve(onSelectSession(decision.sessionId)).then((session) => {
      if (currentScopeRef.current !== attemptScope) {
        return;
      }
      if (!restoredSessionMatchesScope(session, { projectId: effectiveProjectId, sessionId: decision.sessionId })) {
        persistLocation?.(effectiveProjectId, '');
        onCreateSession?.();
      }
    }).catch((error) => {
      if (currentScopeRef.current === attemptScope && shouldClearRestoredSession(error)) {
        persistLocation?.(effectiveProjectId, '');
      }
    });
  }, [activeSessionId, suppressSessionRestore, bootStatus, currentScope, effectiveProjectId, explicitProjectId, location, onCreateSession, onSelectSession, persistLocation]);

  useEffect(() => {
    if (!effectiveProjectId || !activeSessionId) {
      return;
    }
    if (!sessionCanPersist(sessionItems, location, effectiveProjectId, activeSessionId)) {
      return;
    }
    if (text(location.projectId) !== effectiveProjectId || text(location.sessionId) !== activeSessionId) {
      persistLocation?.(effectiveProjectId, activeSessionId);
    }
  }, [activeSessionId, effectiveProjectId, location, persistLocation, sessionItems]);
}
